import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import BillingPortalButton from "./BillingPortalButton";
import AddonsList from "./AddonsList";
import { AgencyContext } from "../AgencyProvider/AgencyProvider";
import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";
import CouponSuccessModal from "../../Modals/CouponSuccessModal";

// One block per logical group. Title + optional description on top, then
// the controls. Keeps every block visually consistent regardless of how
// much UI it contains internally.
function ExtraBlock({ title, description, children }) {
  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
      p={4}
      h="100%"
      display="flex"
      flexDirection="column"
    >
      <Box mb={3}>
        <Text
          fontSize="xs"
          textTransform="uppercase"
          color="gray.500"
          fontWeight="bold"
          letterSpacing="0.5px"
        >
          {title}
        </Text>
        {description && (
          <Text fontSize="sm" color="gray.600" mt={1}>
            {description}
          </Text>
        )}
      </Box>
      <Box flex={1}>{children}</Box>
    </Box>
  );
}

const ExtrasSection = ({ userDetails, setIsLoading }) => {
  const baseUrl = getHostDomain();
  const agency = useContext(AgencyContext);
  const [couponCodeError, setCouponCodeError] = useState(null);
  const [couponCodeSuccessMsg, setCouponCodeSuccessMsg] = useState("");
  const [couponCodeIsLoading, setCouponCodeIsLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");

  const {
    isOpen: isCouponSuccessModalOpen,
    onOpen: onCouponSuccessModalOpen,
    onClose: onCouponSuccessModalClose,
  } = useDisclosure();
  const { buttonColorScheme } = agency;

  const handleOnApplyCouponCode = async () => {
    setCouponCodeIsLoading(true);
    setIsLoading(true);
    try {
      const endpoint = "/api/v1/billing/apply_coupon";
      const urlHit = baseUrl + endpoint;

      const body = { coupon_code: couponCode.toString() };
      const getData = await fetchController(urlHit, "POST", body);
      if (getData.status === "success") {
        setCouponCodeSuccessMsg(getData.message);
        onCouponSuccessModalOpen();
        setCouponCodeError(null);
        setTimeout(() => {
          onCouponSuccessModalClose();
          window.location.reload();
        }, 5000);
      } else {
        setCouponCodeError(getData.message);
      }
    } catch (error) {
      // swallow
    } finally {
      setIsLoading(false);
      setCouponCodeIsLoading(false);
    }
  };

  return (
    <>
      <Stack spacing={4} w="100%">
        {/* ─── Billing portal: full-width row, description left / button right ─── */}
        <ExtraBlock
          title="Billing portal"
          description="View invoices, manage tax details, and review billing history on Stripe."
        >
          <Flex justify="flex-end">
            <BillingPortalButton buttonColorScheme={buttonColorScheme} />
          </Flex>
        </ExtraBlock>

        {/* ─── Addons + Coupon: two columns on md+, stacked on mobile ─── */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <ExtraBlock
            title="Addons"
            description="Purchase extra capacity for voice and other usage-based features."
          >
            <AddonsList
              userDetails={userDetails}
              buttonColorScheme={buttonColorScheme}
            />
          </ExtraBlock>

          <ExtraBlock
            title="Coupon"
            description="Apply a promo or partner code to unlock credits or discounts."
          >
            <Stack spacing={2}>
              <Flex gap={2} align="flex-start">
                <Input
                  placeholder="Coupon code"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value);
                    setCouponCodeError("");
                  }}
                  size="md"
                  flex={1}
                />
                <Button
                  colorScheme={buttonColorScheme}
                  isLoading={couponCodeIsLoading}
                  isDisabled={!couponCode}
                  onClick={handleOnApplyCouponCode}
                >
                  Apply
                </Button>
              </Flex>
              {couponCodeError && (
                <Text color="red.500" fontSize="sm">
                  {couponCodeError}
                </Text>
              )}
            </Stack>
          </ExtraBlock>
        </SimpleGrid>
      </Stack>

      <CouponSuccessModal
        isOpen={isCouponSuccessModalOpen}
        onOpen={onCouponSuccessModalOpen}
        onClose={onCouponSuccessModalClose}
        couponCodeSuccessMsg={couponCodeSuccessMsg}
      />
    </>
  );
};

export default ExtrasSection;
